<script>
import { getBranchList } from '@/api/branch.js'
import {
  createHqInbound,
  createInventoryTransfer,
  getInventoryProductStocks,
} from '@/api/inventory.js'
import AppPagination from '@/components/common/AppPagination.vue'
import { getAdminInfo } from '@/utils/token.js'

const emptyInboundForm = () => ({
  productCode: '',
  productName: '',
  quantity: 1,
  unitCost: 0,
  memo: '',
})

const emptyDispatchForm = () => ({
  productCode: '',
  productName: '',
  toBranchCode: '',
  quantity: 1,
  memo: '',
})

export default {
  name: 'ProductStockManagementPage',
  components: { AppPagination },
  data() {
    return {
      adminInfo: getAdminInfo(),
      search: { keyword: '' },
      items: [],
      total: 0,
      page: 1,
      size: 10,
      loading: false,
      saving: false,
      branches: [],
      inboundDialog: false,
      dispatchDialog: false,
      inboundForm: emptyInboundForm(),
      dispatchForm: emptyDispatchForm(),
      message: '',
      errorMessage: '',
    }
  },
  computed: {
    isHq() {
      return this.adminInfo.adminType === 'HQ'
    },
    branchItems() {
      return this.branches
        .filter(item => item.branchType !== 'HEAD' && item.branchStatus !== 'CLOSED')
        .map(item => ({
          title: item.branchName,
          value: item.branchCode,
        }))
    },
  },
  mounted() {
    this.fetchBranches()
    this.fetchList()
  },
  methods: {
    async fetchBranches() {
      const res = await getBranchList({ page: 1, size: 500 })
      if (res.data?.code === 0) {
        this.branches = res.data.data?.items || []
      }
    },
    async fetchList() {
      this.loading = true
      try {
        const res = await getInventoryProductStocks({
          ...this.search,
          page: this.page,
          size: this.size,
        })
        if (res.data?.code === 0) {
          this.items = res.data.data?.items || []
          this.total = res.data.data?.total || 0
        }
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.page = 1
      this.fetchList()
    },
    handleReset() {
      this.search = { keyword: '' }
      this.page = 1
      this.fetchList()
    },
    onPageChange(page) {
      this.page = page
      this.fetchList()
    },
    onSizeChange(size) {
      this.size = size
      this.page = 1
      this.fetchList()
    },
    openInbound(item) {
      this.message = ''
      this.errorMessage = ''
      this.inboundForm = {
        ...emptyInboundForm(),
        productCode: item.productCode,
        productName: item.productName,
      }
      this.inboundDialog = true
    },
    openDispatch(item) {
      this.message = ''
      this.errorMessage = ''
      this.dispatchForm = {
        ...emptyDispatchForm(),
        productCode: item.productCode,
        productName: item.productName,
      }
      this.dispatchDialog = true
    },
    async submitInbound() {
      if (!this.inboundForm.productCode || Number(this.inboundForm.quantity) <= 0) {
        this.errorMessage = '입고 수량을 확인해주세요.'
        return
      }
      this.saving = true
      this.errorMessage = ''
      try {
        const res = await createHqInbound({
          productCode: this.inboundForm.productCode,
          quantity: Number(this.inboundForm.quantity),
          unitCost: Number(this.inboundForm.unitCost || 0),
          memo: this.inboundForm.memo,
        })
        if (res.data?.code === 0) {
          this.inboundDialog = false
          this.message = '본사 입고가 등록되었습니다.'
          await this.fetchList()
        } else {
          this.errorMessage = res.data?.message || '본사 입고 등록에 실패했습니다.'
        }
      } catch (error) {
        this.errorMessage = error.response?.data?.message || '본사 입고 등록에 실패했습니다.'
      } finally {
        this.saving = false
      }
    },
    async submitDispatch() {
      if (!this.dispatchForm.toBranchCode || Number(this.dispatchForm.quantity) <= 0) {
        this.errorMessage = '불출 지점과 수량을 확인해주세요.'
        return
      }
      this.saving = true
      this.errorMessage = ''
      try {
        const res = await createInventoryTransfer({
          productCode: this.dispatchForm.productCode,
          toBranchCode: this.dispatchForm.toBranchCode,
          quantity: Number(this.dispatchForm.quantity),
          memo: this.dispatchForm.memo,
        })
        if (res.data?.code === 0) {
          this.dispatchDialog = false
          this.message = '지점 불출 요청이 등록되었습니다. 지점 입고확정 후 몰 판매 재고로 반영됩니다.'
          await this.fetchList()
        } else {
          this.errorMessage = res.data?.message || '지점 불출 등록에 실패했습니다.'
        }
      } catch (error) {
        this.errorMessage = error.response?.data?.message || '지점 불출 등록에 실패했습니다.'
      } finally {
        this.saving = false
      }
    },
    formatQty(value) {
      return Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 3 })
    },
    formatMoney(value) {
      return `${Number(value || 0).toLocaleString()}원`
    },
  },
}
</script>

<template>
  <div>
    <div class="d-flex justify-space-between align-center mb-4">
      <div>
        <h1 class="page-title">상품별 재고관리</h1>
        <p class="page-desc">본사 입고와 지점 불출을 관리합니다. 본사 재고는 몰 판매 재고로 잡히지 않고, 지점 입고확정 후 판매 가능 재고가 됩니다.</p>
      </div>
    </div>

    <v-alert v-if="message" type="success" variant="tonal" class="mb-3" closable @click:close="message = ''">
      {{ message }}
    </v-alert>
    <v-alert v-if="errorMessage" type="error" variant="tonal" class="mb-3" closable @click:close="errorMessage = ''">
      {{ errorMessage }}
    </v-alert>

    <section class="search-panel">
      <div class="search-grid">
        <v-text-field
          v-model="search.keyword"
          placeholder="상품명, 카테고리, 브랜드 검색"
          prepend-inner-icon="mdi-magnify"
          variant="outlined"
          density="compact"
          hide-details
          @keyup.enter="handleSearch"
        />
        <div class="search-actions">
          <v-btn color="primary" prepend-icon="mdi-magnify" @click="handleSearch">검색</v-btn>
          <v-btn variant="outlined" prepend-icon="mdi-refresh" @click="handleReset">초기화</v-btn>
        </div>
      </div>
    </section>

    <section class="list-panel">
      <div class="list-title">
        <span>상품별 재고 현황</span>
        <v-chip size="small" color="primary" variant="tonal">{{ total }}건</v-chip>
      </div>
      <v-progress-linear v-if="loading" indeterminate color="primary" />
      <v-table class="dark-table" density="comfortable">
        <thead>
          <tr>
            <th>이미지</th>
            <th>상품명</th>
            <th>카테고리</th>
            <th>브랜드</th>
            <th class="text-right">본사 재고</th>
            <th class="text-right">지점 재고</th>
            <th class="text-right">몰 판매가능</th>
            <th class="text-right">판매 지점</th>
            <th v-if="isHq" class="text-center">관리</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in items" :key="item.productCode">
            <td>
              <div class="thumb">
                <v-img v-if="item.thumbnailUrl" :src="item.thumbnailUrl" cover />
                <v-icon v-else color="grey">mdi-image-off-outline</v-icon>
              </div>
            </td>
            <td><strong>{{ item.productName }}</strong></td>
            <td>{{ item.categoryName || '-' }}</td>
            <td>{{ item.brandName || '-' }}</td>
            <td class="text-right stock hq">{{ formatQty(item.hqStockQty) }}</td>
            <td class="text-right stock">{{ formatQty(item.branchPhysicalStockQty) }}</td>
            <td class="text-right stock saleable">{{ formatQty(item.mallSaleableStockQty) }}</td>
            <td class="text-right">{{ formatQty(item.saleBranchCount) }}</td>
            <td v-if="isHq" class="text-center">
              <div class="row-actions">
                <v-btn size="small" color="primary" variant="tonal" @click="openInbound(item)">본사입고</v-btn>
                <v-btn size="small" color="warning" variant="tonal" @click="openDispatch(item)">지점불출</v-btn>
              </div>
            </td>
          </tr>
          <tr v-if="!loading && items.length === 0">
            <td :colspan="isHq ? 9 : 8" class="empty-cell">조회된 상품 재고가 없습니다.</td>
          </tr>
        </tbody>
      </v-table>
      <AppPagination :total="total" :page="page" :size="size" @update:page="onPageChange" @update:size="onSizeChange" />
    </section>

    <v-dialog v-model="inboundDialog" max-width="560">
      <v-card class="dialog-card">
        <v-card-title>본사 입고 등록</v-card-title>
        <v-card-text>
          <div class="readonly-box mb-4">
            <span>상품</span>
            <strong>{{ inboundForm.productName }}</strong>
          </div>
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field v-model.number="inboundForm.quantity" type="number" label="입고 수량 *" variant="outlined" min="0.001" step="0.001" />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model.number="inboundForm.unitCost" type="number" label="입고 단가" variant="outlined" min="0" suffix="원" />
            </v-col>
            <v-col cols="12">
              <v-textarea v-model="inboundForm.memo" label="메모" variant="outlined" rows="4" />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="inboundDialog = false">취소</v-btn>
          <v-btn color="primary" :loading="saving" @click="submitInbound">등록</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dispatchDialog" max-width="620">
      <v-card class="dialog-card">
        <v-card-title>지점 불출 등록</v-card-title>
        <v-card-text>
          <div class="readonly-box mb-4">
            <span>상품</span>
            <strong>{{ dispatchForm.productName }}</strong>
          </div>
          <v-alert color="info" variant="tonal" class="mb-4">
            지점 관리자가 입고확정을 해야 몰 판매 가능 재고로 반영됩니다.
          </v-alert>
          <v-row>
            <v-col cols="12" md="7">
              <v-select v-model="dispatchForm.toBranchCode" :items="branchItems" label="불출 지점 *" variant="outlined" />
            </v-col>
            <v-col cols="12" md="5">
              <v-text-field v-model.number="dispatchForm.quantity" type="number" label="불출 수량 *" variant="outlined" min="0.001" step="0.001" />
            </v-col>
            <v-col cols="12">
              <v-textarea v-model="dispatchForm.memo" label="불출 메모" variant="outlined" rows="4" />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="dispatchDialog = false">취소</v-btn>
          <v-btn color="primary" :loading="saving" @click="submitDispatch">등록</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.page-title { font-size: 24px; font-weight: 800; margin: 0; color: #f8fafc; }
.page-desc { margin: 6px 0 0; color: #a9b6c9; font-size: 13px; }
.search-panel, .list-panel { border: 1px solid #334155; background: #1c222e; border-radius: 8px; padding: 16px; }
.search-panel { margin-bottom: 16px; }
.search-grid { display: grid; grid-template-columns: minmax(260px, 1fr) auto; gap: 10px; align-items: center; }
.search-actions { display: flex; gap: 8px; justify-content: flex-end; }
.list-title { display: flex; align-items: center; gap: 8px; font-size: 17px; font-weight: 800; color: #f8fafc; margin-bottom: 12px; }
.dark-table { border: 1px solid #334155; border-radius: 8px; overflow: hidden; background: #111827; }
.dark-table :deep(th) { background: #252d3b !important; color: #dbeafe !important; font-weight: 800 !important; white-space: nowrap; }
.dark-table :deep(td) { color: #e5e7eb !important; border-bottom: 1px solid #243041 !important; vertical-align: middle; }
.thumb { width: 54px; height: 54px; border-radius: 6px; background: #0f172a; border: 1px solid #334155; display: flex; align-items: center; justify-content: center; overflow: hidden; }
.stock { font-weight: 800; }
.stock.hq { color: #fbbf24 !important; }
.stock.saleable { color: #60a5fa !important; }
.row-actions { display: inline-flex; gap: 6px; white-space: nowrap; }
.empty-cell { text-align: center; color: #94a3b8 !important; padding: 36px !important; }
.dialog-card { background: #1c222e !important; color: #e5e7eb !important; border: 1px solid #334155; }
.readonly-box { border: 1px solid #334155; background: #111827; border-radius: 8px; padding: 12px 14px; display: grid; gap: 4px; }
.readonly-box span { color: #94a3b8; font-size: 12px; }
.readonly-box strong { color: #f8fafc; }
@media (max-width: 800px) {
  .search-grid { grid-template-columns: 1fr; }
  .search-actions { justify-content: flex-start; }
}
</style>
